import { FileImage } from "lucide-react";
import { apiFetch } from "@contractor-quoting/api-client";
import { Badge, Card } from "@contractor-quoting/shared-ui";
import type { ProjectDetail } from "../pages/Projects.js";

export function ProjectContextPanel({
  project,
  reload,
}: {
  project: ProjectDetail | null;
  reload: () => Promise<void>;
}) {
  if (!project) return <p className="mt-4 text-sm text-stone-500">Loading context…</p>;
  const money = (minor: string, currency = "CAD") =>
    new Intl.NumberFormat("en-CA", { style: "currency", currency }).format(Number(minor) / 100);
  return (
    <div className="mt-4 space-y-4">
      <Card className="p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-stone-500">Job status</p>
        <p className="mt-2 font-black capitalize">
          {(project.job_status ?? project.status).replaceAll("_", " ")}
        </p>
        <p className="mt-1 text-sm capitalize text-stone-500">
          Payment: {(project.payment_status ?? "not_started").replaceAll("_", " ")}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {(project.tradeScopes ?? []).map((scope) => (
            <Badge key={scope.trade_code}>
              {scope.trade_code.replaceAll("_", " ")} · {scope.coverage}
            </Badge>
          ))}
        </div>
      </Card>
      {project.summary ? (
        <Card className="p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-stone-500">Measurements</p>
          <p className="mt-2 text-2xl font-black">
            {project.summary.quantities.requiredMaterial.squareFeet} sq ft
          </p>
          <img
            className="mt-3 rounded-lg border bg-white"
            alt="Approximate project layout"
            src={`data:image/svg+xml,${encodeURIComponent(project.summary.approximateLayoutSvg)}`}
          />
        </Card>
      ) : null}
      {project.images.length ? (
        <Card className="p-4">
          <div className="flex items-center gap-2 font-bold">
            <FileImage size={17} /> Reference images
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {project.images.map((image) => (
              <a key={image.id} href={image.readUrl} target="_blank" rel="noreferrer">
                <img
                  className="aspect-square rounded-lg object-cover"
                  src={image.readUrl}
                  alt={image.filename}
                />
              </a>
            ))}
          </div>
        </Card>
      ) : null}
      {project.walkthroughs.map((walkthrough) => (
        <Card key={walkthrough.id} className="p-4">
          <p className="font-bold">Walkthrough</p>
          <Badge className="mt-2">{walkthrough.status}</Badge>
          {walkthrough.slots
            .filter((slot) => slot.status === "proposed")
            .map((slot) => (
              <button
                className="mt-2 w-full rounded-lg border bg-white p-2 text-left text-sm hover:border-emerald-600"
                key={slot.id}
                onClick={() =>
                  void apiFetch(`/v1/customer/walkthroughs/${walkthrough.id}/respond`, {
                    method: "POST",
                    body: JSON.stringify({ response: "confirmed", slotId: slot.id }),
                  }).then(reload)
                }
              >
                {new Date(slot.startsAt).toLocaleString()}
              </button>
            ))}
        </Card>
      ))}
      {(project.invoices ?? []).map((invoice) => (
        <Card key={invoice.id} className="p-4">
          <p className="font-bold">Invoice {invoice.invoice_number}</p>
          <p className="mt-2 text-2xl font-black">{money(invoice.total_minor, invoice.currency)}</p>
          <p className="mt-1 text-sm capitalize text-stone-500">
            {invoice.status} · due {new Date(invoice.due_date).toLocaleDateString()}
          </p>
          <div className="mt-3 flex gap-3 text-sm font-bold text-emerald-800 underline">
            {invoice.readUrl ? <a href={invoice.readUrl}>Invoice PDF</a> : null}
            {invoice.receiptReadUrl ? <a href={invoice.receiptReadUrl}>Paid receipt</a> : null}
          </div>
        </Card>
      ))}
    </div>
  );
}
